import React, { useMemo, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, LinearProgress, Typography
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ReplayIcon from '@mui/icons-material/Replay';
import { getCookie } from '../../utils/cookies';
import { postFoto } from '../../services/apis';
import { optimizarImagenWebp } from '../../utils/optimizarImagen';

export const DialogFoto = ({ open, onClose, year, cargarFotos }) => {
    const [form, setForm] = useState({
        observacion: '',
        foto: null,
        year: year
    });
    const [subiendo, setSubiendo] = useState(false);
    const [progreso, setProgreso] = useState(0);

    const previewUrl = useMemo(() => {
        return form.foto ? URL.createObjectURL(form.foto) : null;
    }, [form.foto]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e) => {
        if (subiendo) return;

        const file = e.target.files[0];
        if (file) {
            try {
                const imagenOptimizada = await optimizarImagenWebp(file);
                setForm(prev => ({ ...prev, foto: imagenOptimizada }));
            } catch (error) {
                console.error('Error en optimitzar la imatge:', error);
                setForm(prev => ({ ...prev, foto: file }));
            }
        }
    };

    const handleRepeatPhoto = () => {
        if (subiendo) return;

        setForm(prev => ({ ...prev, foto: null }));
    };

    const handleSubmit = async () => {
        if (!form.foto || subiendo) return;

        const formData = new FormData();
        formData.append('token', getCookie('PHPSESSID'));
        formData.append('descripcion', form.observacion);
        if (form.foto) formData.append('foto', form.foto);

        setSubiendo(true);
        setProgreso(0);

        try {
            const response = await postFoto(formData, (progressEvent) => {
                if (!progressEvent.total) {
                    return;
                }

                const porcentaje = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setProgreso(Math.min(100, porcentaje));
            });

            if (response) {
                setProgreso(100);
                await cargarFotos();
                setSubiendo(false);
                setForm({ observacion: '', foto: null, year });
                setProgreso(0);
                onClose();
                return;
            } else {
                console.log('Resposta buida en pujar la foto');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setSubiendo(false);
        }
    };

    const handleClose = () => {
        if (subiendo) return;

        setForm({ observacion: '', foto: null, year });
        setProgreso(0);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle className="photo-dialog-title">
                Nova publicació
            </DialogTitle>

            <DialogContent>
                <Box className="photo-upload-form">
                    {!form.foto ? (
                        <Button variant="outlined" component="label" className="photo-upload-dropzone" disabled={subiendo}>
                            <AddPhotoAlternateIcon />
                            <Typography>Seleccionar foto</Typography>
                            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                        </Button>
                    ) : (
                        <Box className="photo-preview-block">
                            <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={handleRepeatPhoto} disabled={subiendo}>
                                Canviar foto
                            </Button>
                            <img
                                src={previewUrl}
                                alt="Previsualització"
                                className="photo-upload-preview"
                            />
                        </Box>
                    )}

                    <TextField
                        label="Descripció"
                        name="observacion"
                        value={form.observacion}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={3}
                        disabled={subiendo}
                    />

                    {subiendo && (
                        <Box className="photo-upload-progress">
                            <LinearProgress aria-label="Carregant..." />
                            <Typography>{progreso}% pujat</Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions className="photo-dialog-actions">
                <Button onClick={handleClose} disabled={subiendo}>Cancel·lar</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!form.foto || subiendo}
                >
                    {subiendo ? 'Pujant...' : 'Publicar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
